import * as React from 'react';
import { AlertTriangle, CheckCircle2, ShieldAlert } from 'lucide-react';

import { Button } from '../primitives/button';
import { cn } from '../lib/utils';
import { DegradedPanel } from './degraded-state-panel';
import {
  summarizeSaga,
  type CommandReceipt,
  type OptimisticConflict,
  type SagaStep,
  type SagaSummary,
} from './degraded-state-core';

/**
 * Surfaces for what a COMMAND actually did: a partially-applied saga, an
 * idempotent replay, and a lost optimistic write.
 *
 * These are the states an operator acts on next, so each one has to be
 * unmistakable from both "it worked" and "nothing happened" — the two
 * readings that lead to a double-post or an abandoned reconciliation.
 */

// ─── Partial saga ─────────────────────────────────────────────────────

const STEP_TONE: Record<SagaStep['status'], string> = {
  pending: 'text-muted-foreground',
  running: 'text-muted-foreground',
  succeeded: 'text-success',
  failed: 'text-destructive',
  compensated: 'text-warning',
  skipped: 'text-muted-foreground',
};

export interface PartialSagaStateProps {
  steps: ReadonlyArray<SagaStep>;
  /** Retry only the steps that did not apply. Preferred over a blind retry. */
  onRetryOutstanding?: (outstandingStepIds: string[]) => void;
  /** Compensate/roll back the steps that DID apply. */
  onCompensate?: (appliedStepIds: string[]) => void;
  /**
   * Domain-specific recovery control, rendered first in the footer. Use this
   * when neither a step-retry nor a compensation is the right next move —
   * e.g. "continue the draft that was created" — rather than relabelling
   * the generic retry into something it does not do.
   */
  recoveryAction?: React.ReactNode;
  correlationId?: string;
  className?: string;
}

/**
 * Renders what a multi-step command actually did. The whole point is that
 * a partially-applied saga must never look like either a success or a
 * clean failure — both invite the wrong next action.
 */
export function PartialSagaState({
  steps,
  onRetryOutstanding,
  onCompensate,
  recoveryAction,
  correlationId,
  className,
}: PartialSagaStateProps) {
  const summary: SagaSummary = summarizeSaga(steps);
  if (summary.outcome === 'complete') return null;

  const title =
    summary.outcome === 'partial'
      ? 'Partly applied — this needs reconciling'
      : summary.outcome === 'none-applied'
        ? 'Nothing was applied'
        : 'Still running';

  return (
    <DegradedPanel
      tone={summary.outcome === 'partial' ? 'danger' : 'warning'}
      icon={<AlertTriangle className="h-4 w-4" />}
      title={title}
      testId="degraded-partial-saga"
      assertive={summary.outcome === 'partial'}
      className={className}
      footer={
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {recoveryAction}
          {onRetryOutstanding && summary.outstandingStepIds.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onRetryOutstanding(summary.outstandingStepIds)}
              data-testid="degraded-saga-retry-outstanding"
            >
              Retry the {summary.outstandingStepIds.length} remaining step
              {summary.outstandingStepIds.length === 1 ? '' : 's'}
            </Button>
          )}
          {onCompensate && summary.appliedStepIds.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onCompensate(summary.appliedStepIds)}
              data-testid="degraded-saga-compensate"
            >
              Roll back the {summary.appliedStepIds.length} applied step
              {summary.appliedStepIds.length === 1 ? '' : 's'}
            </Button>
          )}
          {correlationId && (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <span>Reference</span>
              <code
                className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground"
                data-testid="degraded-correlation-id"
              >
                {correlationId}
              </code>
            </span>
          )}
        </div>
      }
    >
      <p data-testid="degraded-saga-summary">
        {summary.succeeded} of {summary.total} steps applied
        {summary.failed > 0 ? `, ${summary.failed} failed` : ''}
        {summary.compensated > 0 ? `, ${summary.compensated} rolled back` : ''}.
        {summary.requiresReconciliation
          ? ' Retrying the whole command would re-apply the steps that already succeeded.'
          : ''}
      </p>
      <ul className="mt-2 space-y-1" data-testid="degraded-saga-steps">
        {steps.map((step) => (
          <li key={step.id} className="flex items-baseline gap-2 text-xs">
            <span className={cn('font-medium', STEP_TONE[step.status])}>
              {step.status}
            </span>
            <span className="text-foreground">{step.label}</span>
            {step.detail && <span className="text-muted-foreground">— {step.detail}</span>}
          </li>
        ))}
      </ul>
    </DegradedPanel>
  );
}

// ─── Duplicate / idempotent receipt ───────────────────────────────────

export interface CommandReceiptPanelProps {
  receipt: CommandReceipt;
  /** Navigate to the record the receipt refers to. */
  onOpen?: () => void;
  className?: string;
}

/**
 * Success receipt that distinguishes "applied now" from "already applied".
 * A replayed idempotent command MUST NOT read as a second posting — that
 * is how an operator double-books a journal entry.
 */
export function CommandReceiptPanel({ receipt, onOpen, className }: CommandReceiptPanelProps) {
  const replayed = receipt.replayed === true;

  return (
    <DegradedPanel
      tone={replayed ? 'info' : 'success'}
      icon={replayed ? <ShieldAlert className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
      title={replayed ? 'Already applied — no second copy was created' : 'Applied'}
      testId={replayed ? 'degraded-duplicate-receipt' : 'degraded-receipt'}
      className={className}
      footer={
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {onOpen && (
            <Button type="button" variant="outline" size="sm" onClick={onOpen}>
              Open record
            </Button>
          )}
          {receipt.correlationId && (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <span>Reference</span>
              <code
                className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground"
                data-testid="degraded-correlation-id"
              >
                {receipt.correlationId}
              </code>
            </span>
          )}
        </div>
      }
    >
      <p>
        {replayed
          ? 'This request matched a command that was already accepted, so the original outcome is shown instead of creating a duplicate.'
          : receipt.summary ?? 'The command was accepted.'}
      </p>
      <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
        <dt className="text-muted-foreground">Receipt</dt>
        <dd className="font-mono" data-testid="degraded-receipt-id">
          {receipt.receiptId}
        </dd>
        {receipt.acceptedAt && (
          <>
            <dt className="text-muted-foreground">
              {replayed ? 'Originally accepted' : 'Accepted'}
            </dt>
            <dd data-testid="degraded-receipt-accepted-at">{receipt.acceptedAt}</dd>
          </>
        )}
        {receipt.idempotencyKey && (
          <>
            <dt className="text-muted-foreground">Idempotency key</dt>
            <dd className="font-mono">{receipt.idempotencyKey}</dd>
          </>
        )}
      </dl>
    </DegradedPanel>
  );
}

// ─── Optimistic conflict ──────────────────────────────────────────────

export interface OptimisticConflictBannerProps {
  conflict: OptimisticConflict | null | undefined;
  /** Discard the local draft and reload the server version. */
  onDiscardMine?: () => void;
  /** Reload the server record into the form, keeping the draft for compare. */
  onReload?: () => void;
  /** Force-apply the local draft over the server version, where permitted. */
  onOverwrite?: () => void;
  className?: string;
}

/**
 * Someone else changed the record while this form was open. The banner
 * shows field-level divergence rather than a bare "conflict", because the
 * operator's decision depends on WHICH field moved.
 */
export function OptimisticConflictBanner({
  conflict,
  onDiscardMine,
  onReload,
  onOverwrite,
  className,
}: OptimisticConflictBannerProps) {
  if (!conflict) return null;

  return (
    <DegradedPanel
      tone="warning"
      icon={<AlertTriangle className="h-4 w-4" />}
      title="This record changed while you were editing"
      testId="degraded-optimistic-conflict"
      assertive
      className={className}
      footer={
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {onReload && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onReload}
              data-testid="degraded-conflict-reload"
            >
              Load their version
            </Button>
          )}
          {onDiscardMine && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onDiscardMine}
              data-testid="degraded-conflict-discard"
            >
              Discard my changes
            </Button>
          )}
          {onOverwrite && (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={onOverwrite}
              data-testid="degraded-conflict-overwrite"
            >
              Keep mine and overwrite
            </Button>
          )}
          {conflict.correlationId && (
            <code
              className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground"
              data-testid="degraded-correlation-id"
            >
              {conflict.correlationId}
            </code>
          )}
        </div>
      }
    >
      <p>
        {conflict.changedBy
          ? `${conflict.changedBy} saved a newer version`
          : 'A newer version was saved'}
        {conflict.changedAt ? ` at ${conflict.changedAt}` : ''}. Your changes have not
        been saved.
      </p>
      {(conflict.baseVersion || conflict.serverVersion) && (
        <p className="mt-1 text-xs text-muted-foreground">
          You edited version{' '}
          <code className="font-mono">{conflict.baseVersion ?? 'unknown'}</code>; the
          current version is{' '}
          <code className="font-mono">{conflict.serverVersion ?? 'unknown'}</code>.
        </p>
      )}
      {conflict.fields.length > 0 && (
        <table className="mt-2 w-full text-xs" data-testid="degraded-conflict-fields">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th scope="col" className="pr-3 font-normal">
                Field
              </th>
              <th scope="col" className="pr-3 font-normal">
                Yours
              </th>
              <th scope="col" className="font-normal">
                Theirs
              </th>
            </tr>
          </thead>
          <tbody>
            {conflict.fields.map((f) => (
              <tr key={f.field}>
                <th scope="row" className="pr-3 text-left font-medium">
                  {f.label ?? f.field}
                </th>
                <td className="pr-3 font-mono">{f.mine}</td>
                <td className="font-mono">{f.theirs}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </DegradedPanel>
  );
}
